import config from '../config.json';
import { logError, logInfo } from '../utils/logger';
import authService from './auth';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectTimeout = null;
    this.reconnectAttempt = 0;
    this.listeners = {
      gyms_update: [],
      gym_update: [],
      gym_details: [],
      error: []
    };
  }

  connect() {
    if (this.socket) {
      this.disconnect();
    }

    if (!authService.isLoggedIn()) {
      logError('Cannot connect: Not authenticated');
      //return;
    }

    const user = authService.getUser();
    const wsUrl = `wss://${config.server_host}:${config.server_port}/ws?user_id=${user.id}&bearer=${encodeURIComponent(config.api_bearer)}`;
    
    try {
      this.socket = new WebSocket(wsUrl);
      
      this.socket.onopen = this.handleOpen.bind(this);
      this.socket.onmessage = this.handleMessage.bind(this);
      this.socket.onclose = this.handleClose.bind(this);
      this.socket.onerror = this.handleError.bind(this);
      
      logInfo('WebSocket connecting...');
    } catch (error) {
      logError('WebSocket connection error:', error);
      this.scheduleReconnect();
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    this.isConnected = false;
  }

  handleOpen() {
    logInfo('WebSocket connected');
    this.isConnected = true;
    this.reconnectAttempt = 0;
    
    // Add authorization header
    this.socket.send(JSON.stringify({
      type: 'authorization',
      token: authService.getAPIBearer()
    }));
  }

  handleMessage(event) {
    try {
      const data = JSON.parse(event.data);
      logInfo('WebSocket message data: ', data);
      
      if (data.type && this.listeners[data.type]) {
        this.listeners[data.type].forEach(callback => callback(data));
      }
    } catch (error) {
      logError('Error parsing WebSocket message:', error);
    }
  }

  handleClose(event) {
    this.isConnected = false;
    
    if (event.code === 1000) {
      logInfo('WebSocket closed normally');
    } else if (event.code === 1008) {
      // Authorization failed
      logError('WebSocket authorization failed');
      authService.logout();
      window.location.reload();
      return;
    } else {
      logError(`WebSocket closed unexpectedly: ${event.code}`, event.reason);
      this.scheduleReconnect();
    }
  }

  handleError(error) {
    logError('WebSocket error:', error);
    // The close handler will be called after this
  }

  scheduleReconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    
    // Exponential backoff with 5s debounce and max of 30s
    const delay = Math.min(5000 * Math.pow(1.5, this.reconnectAttempt), 30000);
    this.reconnectAttempt++;
    
    logInfo(`Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempt})`);
    
    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  updateCanvas(canvas) {
    //logInfo('canvas_update: ', canvas);
    if (!this.isConnected) return;
    
    this.sendMessage({
      type: 'canvas_update',
      canvas
    });
  }

  requestGymDetails(gymId, timeSpan = 'Last Hour') {
    if (!this.isConnected) return;
    
    this.sendMessage({
      type: 'gym_details',
      gym_id: gymId,
      time_span: timeSpan
    });
  }

  updateBadgeLevel(gymId, badgeLevel) {
    if (!this.isConnected) return;
    
    this.sendMessage({
      type: 'update_badge',
      gym_id: gymId,
      badge_level: badgeLevel
    });
  }

  sendMessage(message) {
    if (!this.isConnected || !this.socket) {
      logError('Cannot send message: WebSocket not connected');
      return;
    }
    
    try {
      this.socket.send(JSON.stringify(message));
    } catch (error) {
      logError('Error sending WebSocket message:', error);
    }
  }

  on(type, callback) {
    if (!this.listeners[type]) {
      this.listeners[type] = [];
    }
    this.listeners[type].push(callback);
    
    return () => {
      this.listeners[type] = this.listeners[type].filter(cb => cb !== callback);
    };
  }

  off(type, callback) {
    if (!this.listeners[type]) return;
    this.listeners[type] = this.listeners[type].filter(cb => cb !== callback);
  }
}

export default new WebSocketService();